import RightContent from '@/components/RightContent';
import { getApiMerchantInfoGet } from '@/services/ytt_apis/apiMerchant/info';
import {
  getLoginFailedTime,
  getQueryString,
  getToken,
  isShowLogo,
  isTechDomain,
  isTrataoDomain,
  setToken,
} from '@/utils/utils';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { RouteContext } from '@ant-design/pro-components';
import { history, Link, setLocale, request as umiRequest, useLocation } from '@umijs/max';
import { ConfigProvider, notification, Tooltip } from 'antd';
import { stringify } from 'querystring';
import React, { useEffect } from 'react';
import { fixContext } from 'react-activation';
import type { RequestConfig, RunTimeLayoutConfig } from 'umi';
import type { RequestOptionsInit } from 'umi-request';
import defaultSettings from '../config/defaultSettings';
import conf from '../config/deploy';
import logo from './assets/svg/public/logo.svg';
import techLogo from './assets/svg/public/tech_logo.png';
import UnAccessible from './components/UnAccessible';
import { outLogin, currentUser as queryCurrentUser } from './services/ant-design-pro/api';
import { getTAuthUserInfo, queryUserRoles } from './services/auth/auth';
import {
  canAccessMerchantCompanyInformation,
  isMerchantCompanyInformationRoute,
  shouldShowUnassignedRoleGuidance,
} from './utils/merchantCompanyInformation';
import { getCountryCodeFormServer, getPartnerConfigFormServer } from './utils/remote';

const isDev = process.env.REACT_APP_ENV === 'dev';

const loginPath = '/tauth/login';

const environment = conf?.env;

/** 商户 RBAC 上下文，对应后端 MerchantRbacContext */
type MerchantRbacState = {
  rbacScope: boolean;
  status: string;
  merchantAdmin: boolean;
  apiIdentity: boolean;
  primaryAdmin: boolean;
  roleIds: string[];
  routes: string[];
  permissions: string[];
};

/**
 * 读取当前账号的商户 RBAC 上下文。
 * 该接口直接返回 MerchantRbacContext，不是 { status, data } 包装。
 * 取不到时按「不在 RBAC 范围」处理、退回旧菜单规则：服务端仍会独立鉴权，
 * 这里失败不会提权；反之若把异常抛给外层 catch，会被当成未登录踢回登录页。
 */
const fetchMerchantRbac = async (): Promise<MerchantRbacState | undefined> => {
  try {
    return await umiRequest<MerchantRbacState>('/api/merchant/member/self-permission', {
      method: 'GET',
      skipErrorHandler: true,
    });
  } catch (error) {
    console.warn('Failed to load merchant RBAC context:', error);
    return undefined;
  }
};

/** 允许访问路径 */
const allowList: string[] = [
  loginPath,
  '/user/login',
  '/tauth/callback',
  '/tauth/login/',
  '/tauth/callback/',
  '/exception/403',
  '/exception/401',
  '/exception/403/',
  '/exception/401/',
  '/esign/esign-index',
  '/esign',
  '/compliance/onboarding',
];

/** 获取用户信息比较慢的时候会展示一个 loading */
// Note: initialStateConfig is no longer needed in UMI 4

const LayoutWrapper = function ({ children }) {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    const antLayoutContainer = Array.from(document.getElementsByClassName('ant-layout')).pop();
    const logoContainer = document.querySelector('.ant-pro-global-header-logo');
    const avaterContainer = document.querySelector('.ant-space-align-center');

    if (!antLayoutContainer) return;
    // 判断路径，添加或移除类名
    if (
      path?.startsWith('/company-management/add') ||
      path?.startsWith('/company-management/due-diligence-form') ||
      path?.startsWith('/company-management/aml-ctf-scp-questionnaire')
    ) {
      if (logoContainer) {
        logoContainer.classList.add('ant-pro-global-header-logo-custom');
      }
      antLayoutContainer.classList.add('custom_background');
      avaterContainer?.classList.add('dark');
    } else {
      antLayoutContainer.classList.remove('custom_background');
      logoContainer?.classList.remove('ant-pro-global-header-logo-custom');
      avaterContainer?.classList.remove('dark');
    }
  }, [location.pathname]);
  return <>{children}</>;
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  logout?: () => Promise<boolean | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      let currentUser;
      let merchantInfo;
      // 获取远程配置
      await getPartnerConfigFormServer();
      await getCountryCodeFormServer();
      if (!isDev) {
        // 顺序不可调整：先取用户 → 再取 RBAC → 最后才取商户信息。
        // /api/merchant/info/get 对「已进入 RBAC 范围但成员不可用」的账号直接返回 403，
        // 若先调它，会被全局 403 处理误导到 /company-management 开户流程。
        currentUser = await getTAuthUserInfo();
        currentUser.token = getToken();
        currentUser.avatar =
          'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png';

        const merchantRbac = await fetchMerchantRbac();
        if (merchantRbac) {
          Object.assign(currentUser, { merchantRbac });
          if (merchantRbac.rbacScope && merchantRbac.status !== 'ACTIVE') {
            history.replace('/exception/403');
            return currentUser;
          }
          if (shouldShowUnassignedRoleGuidance(merchantRbac)) {
            if (location.pathname !== '/rbac/unassigned-role') {
              history.replace('/rbac/unassigned-role');
            }
            return currentUser;
          }
        }

        merchantInfo = await getApiMerchantInfoGet();
      } else {
        const res = await queryCurrentUser();
        currentUser = res.data;
        merchantInfo = await getApiMerchantInfoGet();
      }
      if (merchantInfo && merchantInfo?.status === '1') {
        currentUser['forbidden'] = merchantInfo?.data?.forbidden;
        currentUser['merchant'] = merchantInfo?.data;
        if (
          ['reject', 'onboarding', 'complete'].includes(merchantInfo?.data?.state || 'none') &&
          merchantInfo?.data?.fillStep == 4
        ) {
          if (merchantInfo?.data?.state === 'onboarding') {
            // A pending registration identity may revisit submitted company materials.
            if (
              !canAccessMerchantCompanyInformation((currentUser as any).merchantRbac) ||
              !isMerchantCompanyInformationRoute(location.pathname)
            ) {
              history.push('/welcome');
            }
            currentUser['state'] = merchantInfo?.data?.state;
            return currentUser;
          }
          if (merchantInfo?.data?.state === 'reject' && !location.pathname.includes('/esign')) {
            history.push('/company-management');
            currentUser['state'] = merchantInfo?.data?.state;
            return currentUser;
          }
        } else {
          // if (!location.pathname.includes('/esign') && merchantInfo?.data?.state === 'onboarding') {
          if (!location.pathname.includes('/esign') && merchantInfo?.data?.state !== 'complete') {
            if (currentUser?.roles?.includes('partnerView') || merchantInfo?.data?.previewAccount) {
              history.push('/welcome');
            } else {
              history.push('/company-management');
            }
          }
        }
      } else {
        if (currentUser.roles?.includes('partnerView')) {
          history.push('/welcome');
        } else {
          history.push('/company-management');
        }
      }

      if (!isDev) {
        try {
          const result = await queryUserRoles(
            { userid: currentUser.userid, token: getToken() },
            { skipErrorHandler: true },
          );
          if (result && result.data) {
            const roles = currentUser.roles?.concat(result.data);
            currentUser.roles = roles;
          }
        } catch (error) {
          // 忽略用户角色查询失败，不影响主流程
          console.warn('Failed to query user roles:', error);
        }
      }

      return currentUser;
    } catch (error) {
      // 如果没有登录，重定向到 login
      if (!allowList.includes(location.pathname)) {
        history.push(loginPath);
      }
    }
    return undefined;
  };

  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    const { REACT_APP_ENV } = process.env;
    const { pathname } = history.location;
    if (pathname !== '/tauth/login') {
      if (REACT_APP_ENV !== 'dev') {
        const { authHost } = conf;
        const logoutPath = `${authHost}/tauth/logout`;
        const callback = `${window.location.origin}/exception/401`;
        window.location.href = `${logoutPath}?callback=${callback}`;
      } else {
        await outLogin();
        history.replace({
          pathname: '/tauth/login',
          search: stringify({
            redirect: pathname,
          }),
        });
      }
    }
    return true;
  };

  // 设置语言环境（当前仅支持中英文）
  let locale = localStorage.getItem('umi_locale');
  if (!locale || !['zh-CN', 'en-US'].includes(locale)) {
    locale = navigator.language.startsWith('zh-') ? 'zh-CN' : 'en-US';
    setLocale(locale);
    localStorage.setItem('umi_locale', locale);
  }

  // 设置全局字体
  if (locale === 'zh-CN') {
    // 暂时不设置中文字体
    // document.getElementsByTagName('body')[0].style.setProperty('font-family', 'Source Han Sans CN');
  } else {
    document.getElementsByTagName('body')[0].style.setProperty('font-family', 'xCurrency Type');
  }

  // 如果是白名单或登录页面，不执行
  if (!allowList.includes(history.location.pathname)) {
    const urlParams = new URLSearchParams(history.location.search);
    const token = urlParams.get('token');
    if (token && typeof token === 'string') {
      setToken(token);
    }
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      logout: loginOut,
      settings: defaultSettings,
    };
  }
  return {
    fetchUserInfo,
    logout: loginOut,
    settings: defaultSettings,
  };
}

const authHeaderInterceptor = (url: string, options: RequestOptionsInit) => {
  let authHeader = { token: getToken(), 'TAuth-Token': getToken() } as Record<string, string>;
  const queryToken = getQueryString('token');
  if (location.pathname === '/esign/esign-index' && queryToken) {
    authHeader = {
      token: queryToken,
      'TAuth-Token': queryToken,
    };
  }
  return {
    url: `${url}`,
    options: {
      ...options,
      interceptors: true,
      headers: {
        ...authHeader,
        ...options.headers,
      },
    },
  };
};

const codeMessage = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器异常或没有进行新建、修改数据的操作。',
  401: '用户没有权限（可以尝试退出重新登录）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

// 错误处理函数
const errorHandler = (error: any, opts: any) => {
  if (opts?.skipErrorHandler) throw error;

  // 我们的 errorThrower 抛出的错误
  if (error.name === 'PortalError') {
    const errorInfo = error.info;
    if (errorInfo) {
      const { errorMessage, showType } = errorInfo;
      switch (showType) {
        case 0: // SILENT - 不显示错误
          break;
        case 1: // WARN_MESSAGE - 警告信息
          notification.warning({
            message: '警告',
            description: errorMessage,
          });
          break;
        case 2: // ERROR_MESSAGE - 错误信息
          notification.error({
            message: '错误',
            description: errorMessage,
          });
          break;
        case 4: // NOTIFICATION - 通知提示
          notification.open({
            message: '通知',
            description: errorMessage,
          });
          break;
        case 9: // REDIRECT - 页面跳转
          history.push('/exception/500');
          break;
        default:
          notification.error({
            message: '错误',
            description: errorMessage,
          });
      }
    }
  } else if (error.response) {
    // axios 的错误
    const { response } = error;
    const { status, statusText } = response;
    let errorText = codeMessage[status as keyof typeof codeMessage] || statusText;

    // 处理特定状态码
    if (status === 401) {
      // 忽略用户角色查询接口的错误
      if (error.config.url.includes('/api/backend/manage/api/auth/user/roles')) {
        return;
      }
      // 未授权，跳转登录
      const { pathname } = history.location;
      if (!allowList.includes(pathname)) {
        history.push(loginPath);
      }
      return;
    } else if (status === 403) {
      if (
        response?.config?.url?.includes('/api/merchant/info/get') &&
        !location.pathname?.startsWith('/company-management')
      ) {
        history.push('/company-management');
        return;
      }
      if (location.pathname === '/esign/esign-index') {
        return;
      }

      history.push('/exception/403');
      return;
    }
    // 若返回错误信息带有status字段且不等于1，通知框显示返回的错误信息
    if (error.data && error.data.status && error.data.status !== '1') {
      if (error.data.message) {
        errorText = error.data.message;
      }
    }

    if (
      response &&
      !response?.config?.url?.endsWith('/api/currentUser') &&
      !response?.config?.url?.endsWith('/api/tauth/info') &&
      !response?.config?.url?.endsWith('/api/tauth/token') &&
      !response?.config?.url?.endsWith('/api/merchant/batch/transfer/check/order') &&
      !response?.config?.url?.endsWith('/api/merchant/batch/transfer/status') &&
      !response?.config?.url?.endsWith('/api/merchant/transaction/create') &&
      !response?.config?.url?.endsWith('/api/merchant/case') &&
      !response?.config?.url?.endsWith('/api/merchant/case/batch') &&
      !response?.config?.url?.endsWith('/api/client/xtool/pdf/transaction') &&
      !response?.config?.url?.match(/\/api\/merchant\/transaction\/get\//) &&
      !response?.config?.url?.includes('/api/merchant/transaction/check')
    ) {
      notification.error({
        message: `请求错误 ${response.status}`,
        description: errorText,
      });
    }
    throw error;
  } else if (error.request) {
    // 请求已经成功发起，但没有收到响应
    notification.error({
      message: '网络错误',
      description: '无响应！请重试。',
    });
  } else {
    // 发送请求时出了点问题
    notification.error({
      message: '请求错误',
      description: '请求错误，请重试。',
    });
  }
};

// 错误抛出函数
const errorThrower = (res: any) => {
  const { success, data, errorCode, errorMessage, showType } = res;
  if (!success) {
    const error: any = new Error(errorMessage);
    error.name = 'PortalError';
    error.info = { errorCode, errorMessage, showType, data };
    throw error; // 抛出自制的错误
  }
};

export const request: RequestConfig = {
  timeout: 1000 * 60 * 5,
  // Note: prefix is no longer supported in UMI 4, use baseURL instead
  baseURL: isDev ? '' : defaultSettings.publicPath,
  // 错误配置
  errorConfig: {
    errorHandler,
    errorThrower,
  },
  requestInterceptors: [authHeaderInterceptor as any],
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  const showLogo = isShowLogo();
  const pathIconMap = {
    '/wallets': 'icon-YUAN-circle-fill',
    '/batch-transfers': 'icon-right-square-fill',
    '/business-transactions': 'icon-transaction',
    '/personal-transactions': 'icon-transaction',
    '/payroll-transactions': 'icon-transaction',
    '/balance-transactions': 'icon-transaction',
    '/my-customer': 'icon-kehu',
    '/global-payout': 'icon-global',
    '/global-payin': 'icon-global',
    '/developer': 'icon-database-fill',
    '/risk-control': 'icon-contacts-fill',
    '/e-commerce-platform': 'icon-shop',
    '/company-management': 'icon-team',
    '/kyt': 'icon-contacts-fill',
    '/settings': 'icon-setting',
    '/bank-direct-management': 'icon-money-bank',
    '/global-account-guide': 'icon-document-fill',
  };

  const menuIconRender = (path: string, defaultDom: React.ReactNode) => {
    const iconClass = pathIconMap[path];
    return (
      <div
        className="xcurrency-custom-menu-item"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {/* [iconfont 使用指南](https://www.iconfont.cn/help/detail?spm=a313x.7781069.1998910419.d8cf4382a&helptype=code) */}

        {/* font-class 引用 */}
        <i className={`iconfont ${iconClass}`} />
        <span className="ant-pro-menu-item">{defaultDom}</span>
      </div>
    );
  };
  return {
    menuItemRender(item, defaultDom) {
      return (
        <Tooltip title={item.name} mouseEnterDelay={1}>
          {item.path && Object.keys(pathIconMap).includes(item.path) ? (
            <Link to={item.path}>{menuIconRender(item.path, defaultDom)}</Link>
          ) : (
            <Link to={item.path ?? '/'}>{defaultDom}</Link>
          )}{' '}
        </Tooltip>
      );
    },
    subMenuItemRender(item, defaultDom) {
      return (
        <Tooltip title={item.name} mouseEnterDelay={1}>
          {item.path && Object.keys(pathIconMap).includes(item.path)
            ? menuIconRender(item.path, defaultDom)
            : defaultDom}
        </Tooltip>
      );
    },
    rightContentRender: () => <RightContent />,
    disableContentMargin: false,
    // footerRender: () => <Footer />,
    onPageChange: () => {
      window.scrollTo(0, 0); // 页面切换时默认返回顶部
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && !allowList.includes(location.pathname)) {
        if (getLoginFailedTime() > 1) {
          // history.push('/exception/403');
        } else {
          history.push(loginPath);
        }
      }
    },
    // 自定义 403 页面
    unAccessible: <UnAccessible />,
    ...initialState?.settings,
    token: {
      header: {
        heightLayoutHeader: 48,
      },
      sider: {
        colorTextMenuSelected: 'rgba(42,122,251,1)',
        colorBgMenuItemSelected: 'rgba(230,243,254,1)',
      },
    },
    enableDarkTheme: false,
    contentStyle: {
      backgroundColor: '#eef0f3',
      minHeight: '100vh',
      padding: 0,
    },
    childrenRender: (children) => {
      return <LayoutWrapper>{children}</LayoutWrapper>;
    },
    logo: showLogo ? (
      <img
        src={isTechDomain ? techLogo : logo}
        alt="logo"
        style={{
          height: '21px',
          marginInlineStart: 8,
        }}
      />
    ) : null,
    title: false,
    pageTitleRender: (_, defaultPageTitle) => {
      let defaultTitle = defaultPageTitle ? `${defaultPageTitle} - ` : '';
      const isPartnerDirectCustomer =
        initialState?.currentUser?.roles?.includes('partnerDirectCustomer');

      let myTitle =
        localStorage.getItem('umi_locale') === 'zh-CN'
          ? '极简货币合作伙伴平台'
          : 'xCurrency Hubs Partner Platform';

      const myTitle2 =
        localStorage.getItem('umi_locale') === 'zh-CN'
          ? '极简货币 商户平台'
          : 'Minimalist Currency Merchant Platform';

      if (isPartnerDirectCustomer) myTitle = myTitle2;

      if (isTrataoDomain) {
        myTitle = '';
        defaultTitle = defaultPageTitle;
      }

      return `${defaultTitle}${myTitle}`;
    },
    siderWidth: 256,
    components: {
      Modal: {
        contentBg: '#F5F5F5',
      },
    },
  };
};

fixContext(RouteContext);
fixContext(ConfigProvider.ConfigContext);
